import React, { useState, useRef, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '../../context/AuthContext'
import { authAPI } from '../../services/api'
import { Eye, EyeOff, Mail, Lock, Phone, ArrowRight, Zap, Globe, Store, ShieldCheck, Clock, TrendingUp, MailCheck, RefreshCw } from 'lucide-react'

const leftSteps = [
  { icon: <ShieldCheck size={17} />, title: 'Create account', desc: 'Fill in your basic details to get started.' },
  { icon: <Clock size={17} />, title: 'Complete KYC', desc: 'Verify your identity to unlock all features.' },
  { icon: <TrendingUp size={17} />, title: 'Start accepting payments', desc: 'Go live and start processing transactions.' },
]

/* ── OTP 6-box input component ── */
const OtpInput = ({ value, onChange, disabled }) => {
  const refs = useRef([])
  const digits = value.split('')

  const handleKey = (i, e) => {
    if (e.key === 'Backspace') {
      if (digits[i]) {
        const next = digits.map((d, idx) => idx === i ? '' : d).join('')
        onChange(next)
      } else if (i > 0) {
        refs.current[i - 1]?.focus()
      }
    }
  }

  const handleChange = (i, e) => {
    const val = e.target.value.replace(/\D/g, '').slice(-1)
    const next = digits.map((d, idx) => idx === i ? val : d)
    // fill blanks
    while (next.length < 6) next.push('')
    onChange(next.join(''))
    if (val && i < 5) refs.current[i + 1]?.focus()
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    onChange(pasted.padEnd(6, '').slice(0, 6))
    refs.current[Math.min(pasted.length, 5)]?.focus()
  }

  return (
    <div style={{ display: 'flex', gap: 10, justifyContent: 'center', margin: '24px 0' }}>
      {Array.from({ length: 6 }).map((_, i) => (
        <input
          key={i}
          ref={el => refs.current[i] = el}
          type="text" inputMode="numeric" maxLength={1}
          value={digits[i] || ''}
          onChange={e => handleChange(i, e)}
          onKeyDown={e => handleKey(i, e)}
          onPaste={handlePaste}
          disabled={disabled}
          style={{
            width: 46, height: 52, textAlign: 'center',
            fontSize: 22, fontWeight: 800, color: '#14142b',
            background: digits[i] ? '#f5f0ff' : '#fff',
            border: `1.5px solid ${digits[i] ? '#7c3aed' : '#ddddf0'}`,
            borderRadius: 10, outline: 'none',
            transition: 'all .15s', boxShadow: '0 1px 3px rgba(20,20,60,0.06)',
            caretColor: 'transparent',
          }}
          onFocus={e => e.target.style.borderColor = '#7c3aed'}
          onBlur={e => e.target.style.borderColor = digits[i] ? '#7c3aed' : '#ddddf0'}
        />
      ))}
    </div>
  )
}

const Register = () => {
  const navigate = useNavigate()
  const { login } = useAuth()
  const [searchParams] = useSearchParams()
  const referralCode = searchParams.get('ref') || ''

  const [step, setStep]             = useState('form')   // 'form' | 'otp'
  const [accountType, setAccountType] = useState('online')
  const [form, setForm]             = useState({ email: '', phone: '', password: '' })
  const [registeredEmail, setRegisteredEmail] = useState('')
  const [otpValue, setOtpValue]     = useState('')
  const [showPw, setShowPw]         = useState(false)
  const [error, setError]           = useState('')
  const [fieldErrors, setFieldErrors] = useState({})
  const [loading, setLoading]       = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)

  // Resend countdown timer
  useEffect(() => {
    if (resendCooldown <= 0) return
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000)
    return () => clearTimeout(t)
  }, [resendCooldown])

  const onChange = (e) => {
    const { name, value } = e.target
    setForm(p => ({ ...p, [name]: value }))
    if (fieldErrors[name]) setFieldErrors(p => { const n = { ...p }; delete n[name]; return n })
    if (error) setError('')
  }

  const validate = () => {
    const errs = {}
    if (!form.email) errs.email = ['Email is required']
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = ['Invalid email address']
    if (!form.phone) errs.phone = ['Phone is required']
    else if (!/^\d{10}$/.test(form.phone)) errs.phone = ['Must be exactly 10 digits']
    if (!form.password) errs.password = ['Password is required']
    else if (form.password.length < 6) errs.password = ['Minimum 6 characters']
    return errs
  }

  // Step 1 — Register
  const onSubmit = async (e) => {
    e.preventDefault()
    const errs = validate()
    if (Object.keys(errs).length) { setFieldErrors(errs); return }
    setLoading(true); setError('')
    try {
      const username = (form.email.split('@')[0] || 'user') + '_' + Date.now().toString().slice(-6)
      const role = accountType === 'pos' ? 'pos_merchant' : 'partner'
      await authAPI.register({ username, email: form.email, phone: form.phone, password: form.password, user_role: role, referral_code: referralCode || undefined })
      setRegisteredEmail(form.email)
      setOtpValue('')
      setStep('otp')
      setResendCooldown(30)
    } catch (err) {
      const data = err.response?.data
      if (data?.errors) setFieldErrors(data.errors)
      else setError(data?.message || 'Registration failed. Please try again.')
    } finally { setLoading(false) }
  }

  // Step 2 — Verify OTP
  const onVerifyOTP = async (e) => {
    e.preventDefault()
    if (otpValue.replace(/\s/g, '').length < 6) { setError('Enter the 6-digit OTP'); return }
    setLoading(true); setError('')
    try {
      const res = await authAPI.verifyOTP(registeredEmail, otpValue, 'registration')
      const d = res.data?.data
      if (d?.auto_login && d?.user && d?.token) {
        login(d.user, d.token)
      }
      navigate('/user/kyc', { replace: true })
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid or expired OTP. Please try again.')
    } finally { setLoading(false) }
  }

  // Resend OTP
  const onResend = async () => {
    if (resendCooldown > 0) return
    setError(''); setOtpValue('')
    try {
      await authAPI.sendOTP(registeredEmail, 'registration')
      setResendCooldown(30)
    } catch {
      setError('Failed to resend OTP. Please try again.')
    }
  }

  const fe = (n) => fieldErrors[n]?.[0]

  const LeftPanel = () => (
    <div className="auth-left">
      <div className="auth-left-inner">
        <Link to="/" className="auth-brand">
          <div className="auth-brand-icon"><Zap size={18} strokeWidth={2.5} /></div>
          <span>A1X<span className="auth-brand-accent">PAY</span></span>
        </Link>
        <motion.div className="auth-left-content"
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}>
          <div className="auth-eyebrow">Get started in minutes</div>
          <h2 className="auth-left-heading">Start accepting payments with A1XPAY</h2>
          <p className="auth-left-sub">Set up your payment gateway in three simple steps and start processing transactions today.</p>
          <div className="auth-steps">
            {leftSteps.map((s, i) => (
              <motion.div key={i} className="auth-step"
                initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.5 + i * 0.12 }}>
                <div className="auth-step-icon">{s.icon}</div>
                <div>
                  <div className="auth-step-title">{s.title}</div>
                  <div className="auth-step-desc">{s.desc}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
      <div className="auth-blob auth-blob-1" />
      <div className="auth-blob auth-blob-2" />
    </div>
  )

  return (
    <>
      <style>{css}</style>
      <div className="auth-page">
        <LeftPanel />

        <div className="auth-right">
          <AnimatePresence mode="wait">

            {/* ── STEP 1: REGISTRATION FORM ── */}
            {step === 'form' && (
              <motion.div key="form" className="auth-card"
                initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.35 }}>

                <div className="auth-card-header">
                  <h1 className="auth-card-title">Create account</h1>
                  <p className="auth-card-sub">Choose your account type to get started</p>
                </div>

                <div className="auth-type-row">
                  {[
                    { id: 'online', Icon: Globe, label: 'Online', sub: 'E-commerce & digital' },
                    { id: 'pos', Icon: Store, label: 'POS / Store', sub: 'Retail & in-store' },
                  ].map(({ id, Icon, label, sub }) => (
                    <button key={id} type="button" onClick={() => setAccountType(id)}
                      className={`auth-type-btn${accountType === id ? ' active' : ''}`}>
                      <Icon size={18} />
                      <div className="auth-type-label">{label}</div>
                      <div className="auth-type-sub">{sub}</div>
                    </button>
                  ))}
                </div>

                {error && (
                  <motion.div className="auth-error" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}>
                    {error}
                  </motion.div>
                )}

                <form onSubmit={onSubmit}>
                  <AuthField label="Email address" error={fe('email')}>
                    <div className="auth-input-wrap">
                      <Mail size={14} className="auth-input-icon" />
                      <input type="email" name="email" value={form.email} onChange={onChange}
                        placeholder="you@company.com" className={`auth-input${fe('email') ? ' error' : ''}`}
                        disabled={loading} autoComplete="email" required />
                    </div>
                  </AuthField>

                  <AuthField label="Phone number" error={fe('phone')} hint="10-digit mobile number">
                    <div className="auth-input-wrap">
                      <Phone size={14} className="auth-input-icon" />
                      <input type="tel" name="phone" value={form.phone} onChange={onChange}
                        placeholder="9876543210" maxLength="10"
                        className={`auth-input${fe('phone') ? ' error' : ''}`}
                        disabled={loading} required />
                    </div>
                  </AuthField>

                  <AuthField label="Password" error={fe('password')} hint="Minimum 6 characters">
                    <div className="auth-input-wrap">
                      <Lock size={14} className="auth-input-icon" />
                      <input type={showPw ? 'text' : 'password'} name="password" value={form.password}
                        onChange={onChange} placeholder="••••••••"
                        className={`auth-input auth-input-pw${fe('password') ? ' error' : ''}`}
                        disabled={loading} autoComplete="new-password" required />
                      <button type="button" className="auth-eye" onClick={() => setShowPw(p => !p)} tabIndex={-1}>
                        {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                  </AuthField>

                  <button type="submit" disabled={loading} className="auth-submit">
                    {loading ? <span className="auth-spinner" /> : <>Create Account <ArrowRight size={14} /></>}
                  </button>
                </form>

                <p className="auth-switch">
                  Already have an account?{' '}
                  <Link to="/login" className="auth-switch-link">Sign in →</Link>
                </p>
              </motion.div>
            )}

            {/* ── STEP 2: OTP VERIFICATION ── */}
            {step === 'otp' && (
              <motion.div key="otp" className="auth-card"
                initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.35 }}>

                {/* Icon */}
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                  <div style={{
                    width: 64, height: 64, borderRadius: 18,
                    background: '#f5f0ff', display: 'inline-flex',
                    alignItems: 'center', justifyContent: 'center',
                  }}>
                    <MailCheck size={28} color="#7c3aed" strokeWidth={1.8} />
                  </div>
                </div>

                <div className="auth-card-header" style={{ textAlign: 'center' }}>
                  <h1 className="auth-card-title">Verify your email</h1>
                  <p className="auth-card-sub">
                    We sent a 6-digit OTP to<br />
                    <strong style={{ color: '#14142b' }}>{registeredEmail}</strong>
                  </p>
                </div>

                {error && (
                  <motion.div className="auth-error" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}>
                    {error}
                  </motion.div>
                )}

                <form onSubmit={onVerifyOTP}>
                  <OtpInput value={otpValue} onChange={setOtpValue} disabled={loading} />

                  <button type="submit" disabled={loading || otpValue.length < 6} className="auth-submit">
                    {loading ? <span className="auth-spinner" /> : <>Verify & Continue <ArrowRight size={14} /></>}
                  </button>
                </form>

                {/* Resend */}
                <div style={{ textAlign: 'center', marginTop: 18, fontSize: 13, color: '#6e6e86' }}>
                  Didn't receive the OTP?{' '}
                  {resendCooldown > 0 ? (
                    <span style={{ color: '#a0a0bc' }}>Resend in {resendCooldown}s</span>
                  ) : (
                    <button onClick={onResend}
                      style={{ background: 'none', border: 'none', color: '#7c3aed', fontWeight: 600, cursor: 'pointer', fontSize: 13, padding: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <RefreshCw size={12} /> Resend OTP
                    </button>
                  )}
                </div>

                {/* Back */}
                <div style={{ textAlign: 'center', marginTop: 10 }}>
                  <button onClick={() => { setStep('form'); setError(''); setOtpValue('') }}
                    style={{ background: 'none', border: 'none', color: '#6e6e86', fontSize: 13, cursor: 'pointer', padding: 0 }}>
                    ← Back to registration
                  </button>
                </div>
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </div>
    </>
  )
}

const AuthField = ({ label, error, hint, children }) => (
  <div className="auth-field">
    <label className="auth-label">{label}</label>
    {children}
    {error ? <div className="auth-field-error">{error}</div> : hint && <div className="auth-field-hint">{hint}</div>}
  </div>
)

const css = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.auth-page {
  display: flex; min-height: 100vh;
  font-family: 'Plus Jakarta Sans', -apple-system, sans-serif;
}

/* ── Left panel ── */
.auth-left {
  display: none; flex: 0 0 34%; position: relative; overflow: hidden;
  background: linear-gradient(175deg, #7c3aed 0%, #5b21b6 55%, #3b0f8e 100%);
}
@media (min-width: 860px) { .auth-left { display: flex; flex-direction: column; } }

.auth-left-inner {
  position: relative; z-index: 2;
  display: flex; flex-direction: column; height: 100%; padding: 2rem 1.75rem;
}
.auth-brand {
  display: flex; align-items: center; gap: 0.5rem;
  text-decoration: none; font-size: 1.1rem; font-weight: 800; color: #fff; letter-spacing: -0.03em;
}
.auth-brand-icon {
  width: 1.875rem; height: 1.875rem; background: rgba(255,255,255,0.18);
  border: 1px solid rgba(255,255,255,0.25); border-radius: 0.4rem;
  display: flex; align-items: center; justify-content: center; color: #fff;
}
.auth-brand-accent { color: #ddd6fe; }
.auth-left-content { flex: 1; display: flex; flex-direction: column; justify-content: center; }
.auth-eyebrow {
  display: inline-block; padding: 0.25rem 0.7rem; margin-bottom: 1rem;
  background: rgba(255,255,255,0.12); border: 1px solid rgba(255,255,255,0.2);
  color: #e9d5ff; font-size: 0.7rem; font-weight: 600;
  letter-spacing: 0.08em; text-transform: uppercase; border-radius: 999px;
}
.auth-left-heading {
  font-size: 1.3rem; font-weight: 800; color: #fff;
  line-height: 1.28; letter-spacing: -0.02em; margin-bottom: 0.7rem; text-wrap: balance;
}
.auth-left-sub { color: rgba(255,255,255,0.6); font-size: 0.82rem; line-height: 1.6; margin-bottom: 1.5rem; }
.auth-steps { display: flex; flex-direction: column; gap: 0.75rem; }
.auth-step { display: flex; align-items: flex-start; gap: 0.6rem; }
.auth-step-icon {
  width: 1.75rem; height: 1.75rem; flex-shrink: 0;
  background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.15);
  border-radius: 0.4rem; display: flex; align-items: center; justify-content: center; color: #ddd6fe;
  margin-top: 2px;
}
.auth-step-title { font-size: 0.82rem; font-weight: 700; color: #fff; margin-bottom: 2px; }
.auth-step-desc { font-size: 0.76rem; color: rgba(255,255,255,0.55); line-height: 1.4; }
.auth-blob { position: absolute; border-radius: 50%; filter: blur(70px); pointer-events: none; }
.auth-blob-1 { width: 300px; height: 300px; background: rgba(124,58,237,0.4); top: -80px; right: -80px; }
.auth-blob-2 { width: 220px; height: 220px; background: rgba(196,181,253,0.18); bottom: 40px; left: -50px; }

/* ── Right panel ── */
.auth-right {
  flex: 1; display: flex; align-items: center; justify-content: center;
  padding: 3rem 4rem; background: #f5f5fa;
}
@media (max-width: 859px) { .auth-right { padding: 2rem 1.25rem; min-height: 100vh; } }

/* ── Form area (no card) ── */
.auth-card {
  width: 100%; max-width: 460px;
}
.auth-card-header { margin-bottom: 22px; }
.auth-card-title { font-size: 1.6rem; font-weight: 800; color: #14142b; letter-spacing: -0.03em; margin-bottom: 6px; }
.auth-card-sub { font-size: 0.9rem; color: #6e6e86; }

/* ── Account type toggle ── */
.auth-type-row { display: flex; gap: 10px; margin-bottom: 16px; }
.auth-type-btn {
  flex: 1; padding: 10px 8px; border: 1.5px solid #e8e8f1; border-radius: 10px;
  background: #fafafa; cursor: pointer; text-align: center; transition: all 0.15s;
  display: flex; flex-direction: column; align-items: center; gap: 3px; color: #6e6e86;
}
.auth-type-btn.active {
  border-color: #7c3aed; background: #f5f0ff; color: #7c3aed;
}
.auth-type-label { font-size: 0.82rem; font-weight: 700; }
.auth-type-sub { font-size: 0.72rem; opacity: 0.75; }

/* ── Form elements ── */
.auth-error {
  background: #fef2f2; color: #b91c1c; border: 1px solid #fecaca;
  border-radius: 10px; padding: 10px 14px; font-size: 0.85rem; margin-bottom: 14px;
}
.auth-field { margin-bottom: 12px; }
.auth-label {
  display: block; font-size: 0.8rem; font-weight: 600;
  color: #4a4a6a; margin-bottom: 5px; letter-spacing: 0.01em;
}
.auth-field-error { font-size: 0.78rem; color: #ef4444; margin-top: 4px; }
.auth-field-hint { font-size: 0.78rem; color: #9090a8; margin-top: 4px; }

.auth-input-wrap { position: relative; }
.auth-input-icon {
  position: absolute; left: 11px; top: 50%; transform: translateY(-50%);
  color: #a0a0bc; pointer-events: none;
}
.auth-input {
  width: 100%; padding: 11px 12px 11px 36px; font-size: 0.9rem;
  font-family: inherit; color: #14142b; background: #fff;
  border: 1.5px solid #ddddf0; border-radius: 10px; outline: none;
  transition: border-color 0.15s, box-shadow 0.15s; box-sizing: border-box;
  box-shadow: 0 1px 3px rgba(20,20,60,0.06);
}
.auth-input:focus { border-color: #7c3aed; box-shadow: 0 0 0 3px rgba(124,58,237,0.10); }
.auth-input.error { border-color: #ef4444; }
.auth-input-pw { padding-right: 38px; }
.auth-eye {
  position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
  background: none; border: none; color: #a0a0bc; cursor: pointer; padding: 4px;
  display: flex; align-items: center;
}
.auth-eye:hover { color: #6e6e86; }

.auth-submit {
  width: 100%; display: flex; align-items: center; justify-content: center; gap: 7px;
  padding: 11px 20px; margin-top: 6px;
  font-size: 0.9rem; font-weight: 700; font-family: inherit;
  color: #fff; background: #7c3aed; border: none; border-radius: 10px;
  cursor: pointer; box-shadow: 0 4px 14px rgba(124,58,237,0.28);
  transition: background 0.2s, transform 0.2s, box-shadow 0.2s;
}
.auth-submit:hover:not(:disabled) {
  background: #6d28d9; transform: translateY(-1px); box-shadow: 0 8px 22px rgba(124,58,237,0.36);
}
.auth-submit:disabled { opacity: 0.65; cursor: not-allowed; }
.auth-spinner {
  width: 16px; height: 16px;
  border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff;
  border-radius: 50%; animation: authspin 0.7s linear infinite; display: inline-block;
}
@keyframes authspin { to { transform: rotate(360deg); } }

.auth-switch { margin-top: 16px; text-align: center; font-size: 0.85rem; color: #6e6e86; }
.auth-switch-link { color: #7c3aed; font-weight: 600; text-decoration: none; }
.auth-switch-link:hover { text-decoration: underline; }

.auth-label-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 5px; }
.auth-label-row .auth-label { margin-bottom: 0; }
.auth-forgot { font-size: 0.8rem; color: #7c3aed; text-decoration: none; font-weight: 500; }
.auth-forgot:hover { text-decoration: underline; }
`

export default Register
